import { shuffle } from "./common";
import { Card, CardId, CardPack, CardTemplate } from "./types";
import { normalizeWeights, pickWeightedRandom } from "./utils/random";
import { GetPlayable } from "./constants";

export type SerializedCardManager = {
  drawPile: SerializedDeck;
  hand: SerializedDeck;
  nextCardId: number;
};

export type SerializedDeck = Card[];

export type CombinableGroup = {
  startIndex: number;
  count: number;
  matchString: string;
  willCombine: boolean; // true if count >= 3 (will auto-combine), false if count == 2 (just highlighting)
};

export type HandCombiningInfo = {
  groups: CombinableGroup[];
  combinableIndices: Set<number>; // indices of cards that should be highlighted
};

export class Deck {
  private _cards: Card[];

  static restore(serial: SerializedDeck): Deck {
    return new Deck(serial);
  }

  serialize(): SerializedDeck {
    return this._cards.map(({ name, cardId }) => ({
      name,
      cardId,
    }));
  }

  constructor(cards: Card[] = []) {
    this._cards = [...cards];
  }

  get length(): number {
    return this._cards.length;
  }

  cards(): Card[] {
    return this._cards.map((c) => ({ ...c }));
  }

  get top(): Card | undefined {
    return this._cards[0];
  }

  add(card: Card): void {
    const idx = this.indexById(card.cardId);
    if (idx > -1)
      throw new Error(`Card ${JSON.stringify(card)} already in deck.`);
    this._cards.unshift(card);
  }

  indexById(id: CardId): number {
    return this._cards.findIndex(({ cardId }) => cardId == id);
  }

  indexByIdOrFail(id: CardId): number {
    const idx = this._cards.findIndex(({ cardId }) => cardId == id);
    if (idx < 0) throw new Error(`Card ${id} missing`);
    return idx;
  }

  swapById(a: CardId, b: CardId): void {
    const aIdx = this.indexByIdOrFail(a),
      bIdx = this.indexByIdOrFail(b);
    [this._cards[aIdx], this._cards[bIdx]] = [
      this._cards[bIdx],
      this._cards[aIdx],
    ];
  }

  moveCardToPosition(cardId: CardId, newIndex: number): void {
    const currentIndex = this.indexByIdOrFail(cardId);
    
    // Clamp newIndex to valid range
    const clampedIndex = Math.max(0, Math.min(newIndex, this._cards.length - 1));
    
    // No-op if moving to same position
    if (currentIndex === clampedIndex) return;
    
    // Remove card from current position
    const [card] = this._cards.splice(currentIndex, 1);
    
    // Insert at new position
    this._cards.splice(clampedIndex, 0, card);
  }

  removeOrFail(id: CardId): Card {
    const idx = this.indexByIdOrFail(id);
    return this._cards.splice(idx, 1)[0];
  }

  shuffle(): this {
    shuffle(this._cards);
    return this;
  }
}

export class CardError extends Error {
  constructor(
    public card: Card,
    message: string,
  ) {
    super(message);
  }
}

export class OutOfCardsError extends Error {
  constructor(message: string) {
    super(message);
  }
}

function detectCombinableGroups(cards: Card[]): CombinableGroup[] {
  if (cards.length < 3) return [];

  const groups: CombinableGroup[] = [];
  const nonActiveCards = cards.slice(1);

  let i = 0;
  while (i < nonActiveCards.length - 1) {
    const playable = GetPlayable(nonActiveCards[i].name);
    const matchString = playable.combines.ownMatchString;

    if (!playable.combines.into) {
      i++;
      continue;
    }

    let count = 1;
    let j = i + 1;

    while (j < nonActiveCards.length) {
      const nextPlayable = GetPlayable(nonActiveCards[j].name);
      if (nextPlayable.combines.ownMatchString === matchString) {
        count++;
        j++;
      } else {
        break;
      }
    }

    if (count >= 2) {
      groups.push({
        startIndex: i + 1,
        count,
        matchString,
        willCombine: count >= 3,
      });
      i = j;
    } else {
      i++;
    }
  }

  return groups;
}

function combineCards(cards: Card[], groups: CombinableGroup[]): Card[] {
  const newCards = [...cards];
  const combiningGroups = groups.filter((group) => group.willCombine);

  for (let i = combiningGroups.length - 1; i >= 0; i--) {
    const group = combiningGroups[i];
    const firstCard = newCards[group.startIndex];
    const playable = GetPlayable(firstCard.name);

    if (playable.combines.into) {
      const combinedCard: Card = {
        cardId: firstCard.cardId,
        name: playable.combines.into,
      };

      newCards.splice(group.startIndex, group.count, combinedCard);
    }
  }

  return newCards;
}

export class CardManager {
  private drawPile: Deck;
  private hand: Deck = new Deck();
  private nextCardId: number = 1;

  serialize(): SerializedCardManager {
    return {
      drawPile: this.drawPile.serialize(),
      hand: this.hand.serialize(),
      nextCardId: this.nextCardId,
    };
  }

  restore(serial: SerializedCardManager) {
    this.drawPile = Deck.restore(serial.drawPile);
    this.hand = Deck.restore(serial.hand);
    this.nextCardId = serial.nextCardId;
  }

  constructor(cards: CardTemplate[]) {
    this.drawPile = new Deck(cards.map(this.newCard.bind(this)));
  }

  newCard(template: CardTemplate): Card {
    return { ...template, cardId: this.nextCardId++ };
  }

  get activeCard(): Card {
    if (!this.hand.top && this.canDraw()) return this.draw();
    if (!this.hand.top)
      throw new OutOfCardsError("No cards in hand and cannot draw!");
    return this.hand.top;
  }

  get cardsInHand(): Card[] {
    return this.getProcessedHandCards();
  }

  private getProcessedHandCards(): Card[] {
    const handCards = this.hand.cards();
    const groups = detectCombinableGroups(handCards);
    const combiningGroups = groups.filter((group) => group.willCombine);

    if (combiningGroups.length > 0) {
      const combinedCards = combineCards(handCards, groups);
      this.hand = new Deck(combinedCards);
      return combinedCards;
    }

    return handCards;
  }

  getHandCombiningInfo(): HandCombiningInfo {
    const cards = this.hand.cards();
    const groups = detectCombinableGroups(cards);
    const combinableIndices = new Set<number>();

    groups.forEach((group) => {
      if (!group.willCombine) {
        // Only highlight cards that won't auto-combine (groups of 2)
        for (
          let i = group.startIndex;
          i < group.startIndex + group.count;
          i++
        ) {
          combinableIndices.add(i);
        }
      }
    });

    return { groups, combinableIndices };
  }

  get maxHandSize(): number {
    return 5;
  }

  canDraw(): boolean {
    return this.hand.length < this.maxHandSize && this.drawPile.length > 0;
  }

  addPack(pack: CardPack, packSize: number, shuffle: boolean = true) {
    generatePack(pack, packSize).forEach((card) =>
      this.drawPile.add(this.newCard(card)),
    );
    if (shuffle) this.drawPile.shuffle();
  }

  drawPileSize() {
    return this.drawPile.length;
  }

  debug() {
    console.log(
      "DRAW",
      this.drawPile.cards().map((c) => c.name),
    );
    console.log(
      "HAND",
      this.hand.cards().map((c) => c.name),
    );
  }

  draw(): Card {
    if (this.hand.length >= this.maxHandSize) {
      throw new Error("Hand is full!");
    }
    const card = this.drawPile.top;
    if (!card) {
      throw new OutOfCardsError("out of cards!");
    }
    this.drawPile.removeOrFail(card.cardId);
    this.hand.add(card);
    return card;
  }

  swapCardsInHand(a: CardId, b: CardId) {
    this.hand.swapById(a, b);
  }

  moveCardInHand(cardId: CardId, newIndex: number) {
    this.hand.moveCardToPosition(cardId, newIndex);
  }

  play(cardId: CardId): Card {
    const card = this.hand.removeOrFail(cardId);
    return card;
  }
}

export const DeckEvents = {
  Deckshuffled: "deckshuffled",
  Carddrawn: "carddrawn",
  Cardplayed: "cardplayed",
} as const;

export function generatePack(pack: CardPack, numCards: number): CardTemplate[] {
  const weights = normalizeWeights(pack.cards);
  return new Array(numCards)
    .fill(1)
    .map(() => pickWeightedRandom(weights).template);
}
